import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

export interface ShopItem {
  id: string;
  name: string;
  description: string | null;
  category: 'board_theme' | 'piece_style' | 'avatar_accessory' | 'profile_decoration';
  price_coins: number;
  price_gems: number;
  item_data: Record<string, any>;
  preview_image: string | null;
  is_active: boolean;
  sort_order: number;
}

export interface UserPurchase {
  id: string;
  user_id: string;
  item_id: string;
  purchased_at: string;
  price_paid_coins: number;
  price_paid_gems: number;
}

export function useShop() {
  const { user } = useAuth();
  const [items, setItems] = useState<ShopItem[]>([]);
  const [purchases, setPurchases] = useState<UserPurchase[]>([]);
  const [equippedItems, setEquippedItems] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);

  const fetchItems = async () => {
    const { data, error } = await supabase
      .from('shop_items')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });

    if (!error && data) {
      setItems(data as ShopItem[]);
    }
  };

  const fetchPurchases = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('user_purchases')
      .select('*')
      .eq('user_id', user.id);

    if (!error && data) {
      setPurchases(data as UserPurchase[]);
    }
  };

  const purchaseItem = async (itemId: string): Promise<boolean> => {
    if (!user) {
      toast.error('Please sign in to make purchases');
      return false;
    }

    setPurchasing(true);

    try {
      const { data, error } = await supabase.rpc('purchase_item', {
        _item_id: itemId
      });

      if (error) {
        if (error.message.includes('insufficient_coins')) {
          toast.error('Not enough coins!');
        } else if (error.message.includes('insufficient_gems')) {
          toast.error('Not enough gems!');
        } else if (error.message.includes('already_purchased')) {
          toast.error('You already own this item!');
        } else {
          toast.error('Purchase failed: ' + error.message);
        }
        return false;
      }

      toast.success(`Purchased ${(data as any)?.item_name}!`);
      
      // Refresh purchases and trigger currency update
      await fetchPurchases();
      window.dispatchEvent(new CustomEvent('currency-updated'));
      
      return true;
    } catch (err) {
      toast.error('Something went wrong');
      return false;
    } finally {
      setPurchasing(false);
    }
  };

  const equipItem = async (itemId: string, category: string): Promise<boolean> => {
    if (!user) return false;

    try {
      // Get current equipped cosmetics
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('equipped_cosmetics')
        .eq('id', user.id)
        .single();

      const currentEquipped = (profile?.equipped_cosmetics as Record<string, string>) || {};
      
      // Toggle equip - if already equipped, unequip
      const newEquipped = { ...currentEquipped };
      const wasEquipped = newEquipped[category] === itemId;
      
      if (wasEquipped) {
        delete newEquipped[category];
        toast.success('Item unequipped');
      } else {
        newEquipped[category] = itemId;
        toast.success('Item equipped! ✨');
      }

      const { error } = await supabase
        .from('user_profiles')
        .update({ equipped_cosmetics: newEquipped })
        .eq('id', user.id);

      if (error) {
        toast.error('Failed to equip item');
        return false;
      }

      // Dispatch event to notify cosmetics hook
      window.dispatchEvent(new CustomEvent('cosmetics-updated'));
      
      // Refresh equipped state
      await fetchEquipped();

      return true;
    } catch (err) {
      toast.error('Something went wrong');
      return false;
    }
  };

  const fetchEquipped = async () => {
    if (!user) {
      setEquippedItems({});
      return;
    }

    const { data: profile } = await supabase
      .from('user_profiles')
      .select('equipped_cosmetics')
      .eq('id', user.id)
      .single();

    setEquippedItems((profile?.equipped_cosmetics as Record<string, string>) || {});
  };

  const isEquipped = (itemId: string): boolean => {
    return Object.values(equippedItems).includes(itemId);
  };

  const isOwned = (itemId: string): boolean => {
    return purchases.some(p => p.item_id === itemId);
  };

  const getItemsByCategory = (category: ShopItem['category']): ShopItem[] => {
    return items.filter(item => item.category === category);
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchItems(), fetchPurchases(), fetchEquipped()]);
      setLoading(false);
    };
    init();
  }, [user]);

  return {
    items,
    purchases,
    equippedItems,
    loading,
    purchasing,
    purchaseItem,
    equipItem,
    isOwned,
    isEquipped,
    getItemsByCategory,
    refreshItems: fetchItems,
    refreshPurchases: fetchPurchases,
    refreshEquipped: fetchEquipped
  };
}
